import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Grid, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

// Animated counter component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [end, duration]);

  return (
    <Typography variant="h3" sx={{ fontWeight: 700, color: 'var(--color-accent)' }}>
      {count}{suffix}
    </Typography>
  );
};

// Particle background
const ParticleBackground = () => {
  const particles = Array.from({ length: 20 });

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {particles.map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            background: `rgba(14, 165, 233, ${0.3 + Math.random() * 0.5})`,
            borderRadius: '50%',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -100, 0],
            x: [0, Math.random() * 50 - 25, 0],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </Box>
  );
};

// Floating tech icons
const FloatingTechIcon = ({ delay, children, position }) => {
  return (
    <motion.div
      style={{
        position: 'absolute',
        ...position,
      }}
      animate={{
        y: [0, -20, 0],
        rotate: [0, 360],
      }}
      transition={{
        duration: 6 + Math.random() * 2,
        repeat: Infinity,
        delay,
      }}
    >
      {children}
    </motion.div>
  );
};

const AnimatedHero = ({ onCtaClick }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';
  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--gradient-hero)',
        color: 'var(--text-light)',
        pt: { xs: 12, md: 16 },
        pb: { xs: 12, md: 16 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 50%, rgba(37, 99, 235, 0.15) 0%, transparent 50%),
                       radial-gradient(circle at 80% 80%, rgba(14, 165, 233, 0.15) 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Particle background */}
      <ParticleBackground />

      {/* Floating tech icons */}
      <FloatingTechIcon
        delay={0}
        position={{ top: '10%', right: '5%', opacity: 0.1 }}
      >
        <Box
          sx={{
            fontSize: '120px',
            color: 'rgba(255, 255, 255, 0.7)',
            filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))',
          }}
        >
          {'</>'}
        </Box>
      </FloatingTechIcon>

      <FloatingTechIcon
        delay={1}
        position={{ bottom: '15%', left: '5%', opacity: 0.1 }}
      >
        <Box
          sx={{
            fontSize: '100px',
            color: 'rgba(255, 255, 255, 0.7)',
            filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))',
          }}
        >
          {'{}'}
        </Box>
      </FloatingTechIcon>

      <FloatingTechIcon
        delay={2}
        position={{ top: '40%', left: '10%', opacity: 0.1 }}
      >
        <Box
          sx={{
            fontSize: '80px',
            color: 'rgba(255, 255, 255, 0.7)',
            filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))',
          }}
        >
          {'◆'}
        </Box>
      </FloatingTechIcon>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left column - Text content */}
          <Grid item xs={12} md={6}>
            <Stack spacing={4}>
              {/* Main heading */}
              <MotionTypography
                component="h1"
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                }}
              >
                {t('hero.title')}
              </MotionTypography>

              {/* Subheading */}
              <MotionTypography
                variant="h5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  fontWeight: 400,
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.6,
                }}
              >
                {t('hero.subtitle')}
              </MotionTypography>

              {/* CTA Button */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
              >
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/contact"
                  endIcon={<ArrowForwardIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                  onClick={onCtaClick}
                  sx={{
                    backgroundColor: 'var(--color-accent)',
                    // fixed navy: the button stays pale in both modes
                    color: 'var(--color-navy)',
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    px: 5,
                    py: 2,
                    borderRadius: 'var(--radius-lg)',
                    textTransform: 'none',
                    boxShadow: '0 12px 30px rgba(14, 165, 233, 0.4)',
                    transition: 'all var(--transition-base)',
                    // MUI spaces the icon for LTR; use logical margins so the
                    // gap sits between text and arrow in Hebrew too
                    '& .MuiButton-endIcon': { mx: 0, marginInlineStart: '10px', marginInlineEnd: '-4px' },
                    '&:hover': {
                      backgroundColor: 'var(--secondary-dark)',
                      color: 'var(--color-navy)',
                      boxShadow: '0 16px 40px rgba(14, 165, 233, 0.5)',
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  {t('hero.cta')}
                </Button>
              </MotionBox>
            </Stack>
          </Grid>

          {/* Right column - Stats */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
            >
              <Stack spacing={6}>
                {/* Stat 1 */}
                <Box>
                  <AnimatedCounter end={150} duration={2.5} suffix="+" />
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      color: 'rgba(255, 255, 255, 0.8)',
                      mt: 1,
                      fontWeight: 600,
                    }}
                  >
                    {t('hero.projects_completed')}
                  </Typography>
                  <Box
                    sx={{
                      height: '3px',
                      background: 'var(--gradient-primary)',
                      borderRadius: 'var(--radius-full)',
                      width: '80px',
                      mt: 2,
                    }}
                  />
                </Box>

                {/* Stat 2 */}
                <Box>
                  <AnimatedCounter end={50} duration={2.5} suffix="+" />
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      color: 'rgba(255, 255, 255, 0.8)',
                      mt: 1,
                      fontWeight: 600,
                    }}
                  >
                    {t('hero.enterprise_clients')}
                  </Typography>
                  <Box
                    sx={{
                      height: '3px',
                      background: 'var(--gradient-primary)',
                      borderRadius: 'var(--radius-full)',
                      width: '60px',
                      mt: 2,
                    }}
                  />
                </Box>

                {/* Stat 3 */}
                <Box>
                  <AnimatedCounter end={25} duration={2.5} suffix="+" />
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      color: 'rgba(255, 255, 255, 0.8)',
                      mt: 1,
                      fontWeight: 600,
                    }}
                  >
                    {t('hero.years_excellence')}
                  </Typography>
                  <Box
                    sx={{
                      height: '3px',
                      background: 'var(--gradient-primary)',
                      borderRadius: 'var(--radius-full)',
                      width: '70px',
                      mt: 2,
                    }}
                  />
                </Box>
              </Stack>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Bottom accent stripe */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'var(--gradient-primary)',
        }}
      />
    </Box>
  );
};

export default AnimatedHero;
